"use client";

import Link from "next/link";
import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import PasswordForm from "./password-form";

import { apiBaseUrl } from "@/lib/browser-api";

type Account = { email: string; roles: string[] };

function csrfToken(): string {
  return decodeURIComponent(
    document.cookie
      .split("; ")
      .find((part) => part.startsWith("lernwerk_csrf="))
      ?.split("=")[1] ?? "",
  );
}

export default function AccountPanel() {
  const router = useRouter();
  const [account, setAccount] = useState<Account>();
  const [state, setState] = useState<"loading" | "signed_out" | "ready" | "error">("loading");
  const [busy, setBusy] = useState<"logout" | "delete">();
  const [message, setMessage] = useState<string>();

  const load = useCallback(async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/auth/me`, { credentials: "include" });
      if (response.status === 401) {
        setState("signed_out");
        return;
      }
      if (!response.ok) throw new Error("account unavailable");
      setAccount((await response.json()) as Account);
      setState("ready");
    } catch {
      setState("error");
    }
  }, []);

  useEffect(() => {
    const initialLoad = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(initialLoad);
  }, [load]);

  async function logout() {
    setBusy("logout");
    setMessage(undefined);
    try {
      const response = await fetch(`${apiBaseUrl}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRF-Token": csrfToken() },
      });
      if (!response.ok) throw new Error("logout unavailable");
      router.push("/login");
    } catch {
      setMessage("Die Abmeldung konnte nicht abgeschlossen werden. Bitte versuche es erneut.");
      setBusy(undefined);
    }
  }

  async function deleteAccount() {
    const confirmed = window.confirm(
      "Konto wirklich löschen? Eigene Aufträge und nicht geteilte Kinderprofile werden dauerhaft entfernt.",
    );
    if (!confirmed) return;
    setBusy("delete");
    setMessage(undefined);
    try {
      const response = await fetch(`${apiBaseUrl}/api/auth/me`, {
        method: "DELETE",
        credentials: "include",
        headers: { "X-CSRF-Token": csrfToken() },
      });
      if (!response.ok) throw new Error("deletion unavailable");
      router.push("/");
    } catch {
      setMessage("Das Konto konnte nicht gelöscht werden. Bitte versuche es erneut.");
      setBusy(undefined);
    }
  }

  if (state === "loading") return <p className="notice">Dein Konto wird geladen …</p>;
  if (state === "signed_out") return <p className="notice">Du bist nicht angemeldet. <Link href="/login">Jetzt anmelden oder ein Konto anlegen</Link>.</p>;
  if (state === "error") return <p className="notice">Dein Konto ist gerade nicht erreichbar. Bitte versuche es gleich noch einmal.</p>;

  return (
    <section className="account-card" aria-labelledby="account-heading">
      <h2 id="account-heading">Angemeldet als</h2>
      <p className="account-email">{account?.email}</p>
      <p className="account-note">Dieses Konto verwaltet Arbeitsblätter und Kinderprofile. Kinder brauchen keine eigene E-Mail-Adresse.</p>
      <div className="actions">
        <Link className="button primary" href="/generate">Arbeitsblatt erstellen</Link>
        <Link className="button secondary" href="/my-worksheets">Meine Blätter</Link>
      </div>
      <div className="account-actions">
        <button className="button secondary" disabled={busy !== undefined} onClick={() => void logout()} type="button">
          {busy === "logout" ? "Wird abgemeldet …" : "Abmelden"}
        </button>
        <button className="button danger" disabled={busy !== undefined} onClick={() => void deleteAccount()} type="button">
          {busy === "delete" ? "Wird gelöscht …" : "Konto löschen"}
        </button>
      </div>
      <PasswordForm />
      <p className="account-delete-note">Die Löschung fragt vorher nach und entfernt eigene Aufträge sowie nicht geteilte Kinderprofile dauerhaft.</p>
      {message ? <p className="job-error" role="status">{message}</p> : null}
    </section>
  );
}
